// In picking-list.component.ts
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { HttpClient } from '@angular/common/http';
import { Location } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { RatingModule } from 'primeng/rating';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { InputIconModule } from 'primeng/inputicon';
import { IconFieldModule } from 'primeng/iconfield';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { ContextMenuModule } from 'primeng/contextmenu';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { UserService } from '../pages/service/user.service'; // <-- Import UserService
import { MessageService } from 'primeng/api';
import { environment } from '../../environments/environment';
import { LoaderService } from '../services/loader.service';
import {
    CreditLimitDialogData,
    CreditLimitDialogKind,
    CreditRestrictionResult,
    formatCreditLimitDialogNote,
    getCreditLimitBypassContactNote,
    logCreditLimitBypassDebug,
    mapCreditRestrictionToDialog,
    resolveCreditLimitPageAction,
    shouldSkipCreditLimitOnPicklist
} from '../item-details/item-utils';
import { CreditLimitDialogContentComponent } from '../item-details/credit-limit-dialog-content/credit-limit-dialog-content.component';
import { InMemoryStorageService } from '../services/in-memory-storage.service';
import { timeout } from 'rxjs';

/** Expanded picklist row for preview/print pagination (item + pre/post text blocks). */
export type PicklistPrintRow =
    | { kind: 'headerPre' | 'headerPost'; text: string }
    | { kind: 'item'; line: any }
    | { kind: 'linePre' | 'linePost'; text: string; parentLineNo: number };

export interface FranceProcessPicklistLine {
    dlix: string;
    plsx: string;
    plrn: string;
    orderLine: string;
    itemNumber: string;
    itemDescription: string;
    /** Order qty (ORQT). Display as Ordered. */
    orderedQuantity: number;
    /** Pick-list qty (TRQT). Display as Picked (read-only); M3 short-pick baseline. */
    originalQuantity: number;
    /** Ship Qty — editable; sent to M3 as QTYP. Defaults to Picked. */
    editableQuantity: number;
    whlo: string;
    whsl: string;
    bano: string;
    camu: string;
    validationError: string;
    processingStatus: string;
}

export interface FranceProcessPicklistGroup {
    dlix: string;
    plsx: string;
    status: string;
    lines: FranceProcessPicklistLine[];
}

@Component({
    selector: 'app-generate-picking-list',
    imports: [
        AutoCompleteModule,
        RouterModule,
        CommonModule,
        TableModule,
        FormsModule,
        ButtonModule,
        ContextMenuModule,
        RippleModule,
        ToastModule,
        ToolbarModule,
        RatingModule,
        InputTextModule,
        TextareaModule,
        SelectModule,
        RadioButtonModule,
        InputNumberModule,
        DialogModule,
        TagModule,
        InputIconModule,
        IconFieldModule,
        ConfirmDialogModule,
        TooltipModule,
        CreditLimitDialogContentComponent
    ],
    templateUrl: './picking-list.component.html',
    styleUrls: ['./picking-list.component.scss'],
    providers: [UserService, MessageService] // <-- Provide UserService and MessageService
})
export class GeneratePickingListComponent implements OnInit, OnDestroy {
    skipReleaseLock = false;
    lockRefreshInterval: any = null;
    item: any;
    itemList: any[] = [];
    totalAmountAfterDiscount = 0;
  
    @ViewChild('pickingList') pickingListElement!: ElementRef;
    div1: string = '';
    supplierDetails = {
        supplerVrno: '',
        coa1: '',
        phno: '',
        coa2: '',
        coa3: '',
        coa4: ''
    };
    customerDetails = {
        customerVrno: '',
        CUA1: '',
        CUA2: '',
        CUA3: '',
        CUA4: '',
        phoneNumber: ''
    };
    pickingListDetails = {
        customerNo: '',
        date: '',
        orderNumber: '',
        referenceOrderNumber: ''
    };
    lineDetails: any[] = [];
    /** Order-level pre/post texts for picklist (CO04 / CO01). */
    headerPreTexts: string[] = [];
    headerPostTexts: string[] = [];
    itemsPerPage = 24; // Preview pagination (unchanged)
    /**
     * Print-only pagination.
     * RCA (missing line 56 on FRA1001007): DESC_CHARS_PER_LINE was 75, so a ~60-char
     * description that wrapped to 2 visual rows was costed as 1. The page overfilled and
     * overflow:hidden clipped the last row (56); next page started at 57.
     * Prefer slightly fewer rows / blank space over ever clipping a line.
     */
    private readonly PRINT_PAGE_LINE_BUDGET = 24;
    /** Chars per visual line in the print designation column (~49% of 210mm). */
    private readonly DESC_CHARS_PER_LINE = 45;
    /** Keep spare visual rows so wrapped lines never clip into the footer. */
    private readonly PRINT_SAFETY_ROWS = 2;
    private readonly SUMMARY_RESERVED_LINES = 4;
    /** Preview + print pages of expanded rows (item / pre / post). */
    paginatedContent: PicklistPrintRow[][] = [];
    /** Used only while capturing print HTML; preview always keeps this false. */
    summaryOnExtraPage = false;
    summary = {
        grossValue: 0,
        totalDiscountValue: 0,
        // totalGrossWeight: 0,
        // totalNetWeight: 0,
        vatAmount: 0,
        totalOrderValue: 0
    };

    totalGrossWeight: number = 0;
    totalNetWeight: number = 0;

    divisionId: string = '';
    creditLimitBlocked = false;
    creditLimitBlockedDialogVisible = false;
    creditLimitProceedDialogVisible = false;
    private creditLimitProceedCallback: (() => void) | null = null;
    private creditLimitAcknowledged = false;
    private marginApprovalAcknowledged = false;
    creditLimitDialogKind: CreditLimitDialogKind = 'exposure';
    creditLimitDialogData: CreditLimitDialogData | null = null;
    creditLimitDialogNood = 0;
    creditLimitDialogOdud = 0;
    creditLimitContactNote = '';

    selectedLanguage = 'en';

    /** France (300) synced: show IDM Ruestliste PDFs instead of Torna HTML. One order may have several. */
    useM3DeliveryNote = false;
    deliveryNotePdfUrls: SafeResourceUrl[] = [];
    private deliveryNoteObjectUrls: string[] = [];
    selectedDeliveryNoteIndex = 0;
    m3DeliveryNoteLoaded = false;
    m3DeliveryNoteError = '';
    m3DeliveryNoteRetryVisible = false;

    /** Picklist not ready in M3/IDM yet — simple retry dialog (no auto-timer). */
    picklistInProgressDialogVisible = false;
    private picklistInProgressParams: {
        orderNumber: string;
        companyId: string;
        divisionId: string;
    } | null = null;

    /** France Process Picklist (short-pick) modal state. */
    processPicklistLoading = false;
    processPicklistDialogVisible = false;
    processPicklistConfirmVisible = false;
    processPicklistProcessing = false;
    processPicklistConfirming = false;
    processPicklistPreviewOrno = '';
    processPicklistGroups: FranceProcessPicklistGroup[] = [];
    processPicklistResults: { plrn: string; itemNumber: string; status: string; errorMessage?: string }[] = [];
    processPicklistProgress = '';
    processPicklistSearch = '';
    /** Confirm-only (no short-pick) dialog. */
    confirmOnlyDialogVisible = false;
    /** When set, confirm uses these keys; empty → backend resolves from M3. */
    confirmOnlyPicklists: { dlix: string; plsx: string }[] = [];
    confirmOnlySource: 'toolbar' | 'popup' = 'toolbar';

    /**
     * After successful France Confirm (or Process which includes Confirm):
     * show invoice-pending dialog, Back to Orders1, auto-redirect after 10s.
     */
    franceConfirmSuccessDialogVisible = false;
    franceConfirmSuccessMessage = '';
    franceConfirmRedirectSeconds = 10;
    /** True while navigating to Orders1 (disables Back; prevents double navigate). */
    franceConfirmNavigating = false;
    private franceConfirmRedirectTimer: ReturnType<typeof setInterval> | null = null;
    private static readonly FRANCE_CONFIRM_REDIRECT_SECONDS = 10;

    labels: { [lang: string]: { [key: string]: string } } = {
        en: {
            pickingListTitle: 'Picking List',
            orderNumber: 'Order No.',
            referenceOrderNumber: 'Reference Order No.',
            customerNo: 'Customer No.',
            date: 'Date',
            lineNo: 'Line No',
            itemNo: 'Item No.',
            quantity: 'Quantity',
            itemName: 'Item Name',
            pcs: 'Pcs',
            size: 'Size',
            packages: 'Packages',
            grossWeight: 'Gross Weight',
            netWeight: 'Net Weight'
        },
        de: {
            pickingListTitle: 'Kommissionierliste',
            orderNumber: 'Auftragsnr.',
            referenceOrderNumber: 'Referenznr.',
            customerNo: 'Kunden-Nr.',
            date: 'Datum',
            lineNo: 'Pos',
            itemNo: 'ArtNr',
            quantity: 'Menge',
            itemName: 'Bezeichnung',
            pcs: 'Stk',
            size: 'Inhalt',
            packages: 'Pakete',
            grossWeight: 'Bruttogewicht',
            netWeight: 'Nettogewicht'
        },
        nl: {
            pickingListTitle: 'Picklijst',
            orderNumber: 'Ordernr.',
            referenceOrderNumber: 'Referentie Ordernr.',
            customerNo: 'Klantnr.',
            date: 'Datum',
            lineNo: 'Positie',
            itemNo: 'Artikelnummer',
            quantity: 'Hoeveelheid',
            itemName: 'Omschrijving',
            pcs: 'Stuks',
            size: 'Inhoud',
            packages: 'Pakketten',
            grossWeight: 'Brutogewicht',
            netWeight: 'Nettogewicht'
        },
        fr: {
            pickingListTitle: 'Liste de prélèvement',
            orderNumber: 'N° de commande',
            referenceOrderNumber: 'N° de commande de référence',
            customerNo: 'N° client',
            date: 'Date',
            lineNo: 'N° ligne',
            itemNo: 'N° article',
            quantity: 'Quantité',
            itemName: 'Désignation',
            pcs: 'Pièces',
            size: 'Taille',
            packages: 'Colis',
            grossWeight: 'Poids brut',
            netWeight: 'Poids net'
        }
    };

    constructor(
        private http: HttpClient,
        private router: Router,
        private userService: UserService,
        private location: Location,
        private loaderService: LoaderService,
        private messageService: MessageService,
        private inMemoryStorage: InMemoryStorageService,
        private cdr: ChangeDetectorRef,
        private sanitizer: DomSanitizer
    ) {
        const nav = this.router.getCurrentNavigation();
        const state = nav?.extras?.state as any;
        this.item = state?.item;
        this.itemList = state?.itemList || [];
        this.creditLimitAcknowledged = state?.creditLimitAcknowledged === true;
        this.marginApprovalAcknowledged = state?.marginApprovalAcknowledged === true;
        console.log('Received item:', this.item);
        console.log('Received itemList:', this.itemList);
    }

    goBack() {
        this.skipReleaseLock = true; // Set flag to skip lock release
        // if (window.history.length > 1) {
        //     this.location.back();
        // } else {
        this.router.navigate(['/Orders1']);
        // }
    }

    ngOnInit() {
        this.startLockRefresh();
        // item is already set from navigation state in the constructor
        const orderNumber = this.item?.orderNumber;
        const companyId = this.item?.companyId || this.userService.getCompanyId() || '';
        const divisionId = this.item?.divisionId || this.userService.getDivisionId() || '';
        this.div1 = divisionId;
        console.log(this.item?.customerName);

        // Set language based on division
        if (divisionId === '100') {
            this.selectedLanguage = 'de';
        } else if (divisionId === '400') {
            this.selectedLanguage = 'nl';
        } else if(divisionId === '300'){
            this.selectedLanguage= 'fr'
        }else {
            this.selectedLanguage = 'en';
        }

        this.fetchPickingListData(orderNumber, companyId, divisionId);
    }

    chunkData(data: any[], size: number): any[][] {
        const chunks = [];
        for (let i = 0; i < data.length; i += size) {
            chunks.push(data.slice(i, i + size));
        }
        return chunks;
    }

    private normalizeTextList(raw: unknown): string[] {
        if (!Array.isArray(raw)) {
            return [];
        }
        return raw
            .map((v) => String(v ?? '').trim())
            .filter((v) => !!v);
    }

    /** Build expanded rows: header PRE → items (line PRE, item, line POST) → header POST. */
    private buildPrintRows(lines: any[], headerPre: string[], headerPost: string[]): PicklistPrintRow[] {
        const rows: PicklistPrintRow[] = [];
        for (const text of headerPre || []) {
            rows.push({ kind: 'headerPre', text });
        }
        for (const line of lines || []) {
            for (const text of this.normalizeTextList(line?.linePreTexts)) {
                rows.push({ kind: 'linePre', text, parentLineNo: Number(line?.position ?? line?.displayNo) || 0 });
            }
            rows.push({ kind: 'item', line });
            for (const text of this.normalizeTextList(line?.linePostTexts)) {
                rows.push({ kind: 'linePost', text, parentLineNo: Number(line?.position ?? line?.displayNo) || 0 });
            }
        }
        for (const text of headerPost || []) {
            rows.push({ kind: 'headerPost', text });
        }
        return rows;
    }

    private getVisualTextLineCost(text: string): number {
        const trimmed = (text ?? '').toString().trim();
        if (!trimmed) {
            return 1;
        }
        return Math.min(3, Math.max(1, Math.ceil(trimmed.length / this.DESC_CHARS_PER_LINE)));
    }

    /** Item row plus attached line pre/post rows for preview page budget. */
    private getLineBlockCost(line: any): number {
        let cost = this.getVisualTextLineCost((line?.description ?? '').toString());
        for (const text of this.normalizeTextList(line?.linePreTexts)) {
            cost += this.getVisualTextLineCost(text);
        }
        for (const text of this.normalizeTextList(line?.linePostTexts)) {
            cost += this.getVisualTextLineCost(text);
        }
        return cost;
    }

    private paginateByLineBlock(lines: any[]): { pages: any[][]; summaryOnExtraPage: boolean } {
        const pages: any[][] = [];
        let current: any[] = [];
        let used = 0;
        const maxUsed = this.PRINT_PAGE_LINE_BUDGET - this.PRINT_SAFETY_ROWS;

        for (const line of lines || []) {
            const cost = this.getLineBlockCost(line);
            if (current.length > 0 && used + cost > maxUsed) {
                pages.push(current);
                current = [];
                used = 0;
            }
            current.push(line);
            used += cost;
        }

        if (pages.length === 0 || current.length > 0) {
            pages.push(current);
        }

        const lastPage = pages[pages.length - 1] || [];
        const lastUsed = lastPage.reduce((sum, l) => sum + this.getLineBlockCost(l), 0);
        const remaining = maxUsed - lastUsed;
        const summaryOnExtraPage = remaining < this.SUMMARY_RESERVED_LINES;

        return { pages, summaryOnExtraPage };
    }

    /**
     * Visual row cost for print pagination.
     * Item descriptions and pre/post text wrap at DESC_CHARS_PER_LINE (cap 3).
     */
    private getRowLineCost(row: PicklistPrintRow | any): number {
        if (row && typeof row === 'object' && 'kind' in row) {
            const printRow = row as PicklistPrintRow;
            if (printRow.kind === 'item') {
                const desc = (printRow.line?.description ?? '').toString().trim();
                if (!desc) {
                    return 1;
                }
                return Math.min(3, Math.max(1, Math.ceil(desc.length / this.DESC_CHARS_PER_LINE)));
            }
            const text = (printRow.text ?? '').toString().trim();
            if (!text) {
                return 1;
            }
            return Math.min(3, Math.max(1, Math.ceil(text.length / this.DESC_CHARS_PER_LINE)));
        }
        // Legacy fallback: raw line object
        const desc = (row?.description ?? '').toString().trim();
        if (!desc) {
            return 1;
        }
        return Math.min(3, Math.max(1, Math.ceil(desc.length / this.DESC_CHARS_PER_LINE)));
    }

    /**
     * Print-only pagination: packs expanded rows until the visual-row budget (minus safety) is full.
     * Wrapped descriptions / pre-post text consume extra budget so content cannot clip under the footer.
     */
    private paginateForPrint(rows: PicklistPrintRow[]): { pages: PicklistPrintRow[][]; summaryOnExtraPage: boolean } {
        const pages: PicklistPrintRow[][] = [];
        let current: PicklistPrintRow[] = [];
        let used = 0;
        const maxUsed = this.PRINT_PAGE_LINE_BUDGET - this.PRINT_SAFETY_ROWS;

        for (const row of rows || []) {
            const cost = this.getRowLineCost(row);
            if (current.length > 0 && used + cost > maxUsed) {
                if (row.kind === 'item') {
                    const movedPres: PicklistPrintRow[] = [];
                    while (current.length > 0 && current[current.length - 1].kind === 'linePre') {
                        const pre = current.pop() as PicklistPrintRow;
                        used -= this.getRowLineCost(pre);
                        movedPres.unshift(pre);
                    }
                    if (current.length > 0) {
                        pages.push(current);
                    }
                    current = [...movedPres];
                    used = movedPres.reduce((sum, r) => sum + this.getRowLineCost(r), 0);
                } else {
                    pages.push(current);
                    current = [];
                    used = 0;
                }
            }
            current.push(row);
            used += cost;
        }

        if (pages.length === 0 || current.length > 0) {
            pages.push(current);
        }

        const lastPage = pages[pages.length - 1] || [];
        const lastUsed = lastPage.reduce((sum, r) => sum + this.getRowLineCost(r), 0);
        const remaining = maxUsed - lastUsed;
        const summaryOnExtraPage = remaining < this.SUMMARY_RESERVED_LINES;

        return { pages, summaryOnExtraPage };
    }

    /** Preview: line-block budget chunks, then expand each page with pre/post for that slice. */
    private buildPreviewPages(lines: any[]): PicklistPrintRow[][] {
        const paginated = this.paginateByLineBlock(lines || []);
        this.summaryOnExtraPage = paginated.summaryOnExtraPage;
        const lineChunks = paginated.pages;
        if (lineChunks.length === 0) {
            return [this.buildPrintRows([], this.headerPreTexts, this.headerPostTexts)];
        }
        return lineChunks.map((chunk, idx) => {
            const isFirst = idx === 0;
            const isLast = idx === lineChunks.length - 1;
            return this.buildPrintRows(
                chunk,
                isFirst ? this.headerPreTexts : [],
                isLast ? this.headerPostTexts : []
            );
        });
    }

    pageHeaderPres(pageRows: PicklistPrintRow[]): string[] {
        return (pageRows || []).filter((r) => r.kind === 'headerPre').map((r) => (r as { text: string }).text);
    }

    pageHeaderPosts(pageRows: PicklistPrintRow[]): string[] {
        return (pageRows || []).filter((r) => r.kind === 'headerPost').map((r) => (r as { text: string }).text);
    }

    pageTableRows(pageRows: PicklistPrintRow[]): PicklistPrintRow[] {
        return (pageRows || []).filter((r) => r.kind === 'item' || r.kind === 'linePre' || r.kind === 'linePost');
    }

    getTotalAmountAfterDiscount(): number {
        return this.itemList.reduce((sum, item) => sum + item.amountAfterDiscount, 0);
    }
    getGrossValue(): number {
        return (this.itemList || []).reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
    }
    getTotalDiscountValue(): number {
        return (this.itemList || []).reduce((sum, row) => sum + ((Number(row.amount) * Number(row.discount)) / 100 || 0), 0);
    }
    getNetAmount(): number {
        return (this.itemList || []).reduce((sum, row) => sum + (Number(row.amountAfterDiscount) || 0), 0);
    }
    getVatAmount(): number {
        return (this.itemList || []).reduce((sum, row) => sum + (Number(row.vatAmount) || (Number(row.amountAfterDiscount) * Number(row.vat)) / 100 || 0), 0);
    }
    getOrderValue(): number {
        return this.getNetAmount() + this.getVatAmount();
    }
    getTotalQuantity(): number {
        return this.lineDetails.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
    }
    getTotalGrossWeight(): number {
        return this.lineDetails.reduce((sum, line) => sum + (Number(line.grossWeight) || 0), 0);
    }
    getTotalNetWeight(): number {
        return this.lineDetails.reduce((sum, line) => sum + (Number(line.netWeight) || 0), 0);
    }
    getAddressLines(address: string): string[] {
        return address
            ? address
                  .split('|')
                  .map((line) => line.trim())
                  .filter((line) => line)
            : [];
    }
getPhoneLines(phone: string): string[] {
    return phone
        ? phone.split('|').map(line => line.trim()).filter(line => line)
        : [];
}
  get headerImage(): string {
        switch (this.div1) {
          case '400': return 'assets/Header.jpg';
          case '300': return 'assets/header_FR.jpg';
          case '100': return 'assets/header_DE.jpg';
          default: return 'assets/Header.jpg';
        }
      }
    get footerImage(): string {
        switch (this.div1) {
          case '400':
            return 'assets/Footer.jpg';
          case '300':
            return 'assets/footer_FR.jpg';
          case '100':
            return 'assets/footer_DE.jpg';
          default:
            return 'assets/Footer.jpg'; // fallback
        }
      }
    printPickingList() {
        if (this.useM3DeliveryNote) {
            const selectedUrl = this.deliveryNoteObjectUrls[this.selectedDeliveryNoteIndex];
            if (!selectedUrl) {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Print unavailable',
                    detail: 'Picklist PDF is not loaded yet.',
                    life: 4000
                });
                return;
            }
            const printWin = window.open(selectedUrl, '_blank');
            if (!printWin) {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Print blocked',
                    detail: 'Please allow pop-ups for this site to print the picklist.',
                    life: 5000
                });
                return;
            }
            printWin.addEventListener('load', () => {
                try {
                    printWin.focus();
                    printWin.print();
                } catch {
                    // User can print from the opened tab.
                }
            });
            return;
        }

        this.messageService.add({
            severity: 'success',
            summary: 'Printing Picking List',
            detail: 'Please wait while the picking list is being prepared for printing.',
            life: 3000
        });

        // Preview stays on fixed 24/page; only the print capture uses wrap-aware pagination.
        const savedPages = this.paginatedContent;
        const savedSummary = this.summaryOnExtraPage;
        const printRows = this.buildPrintRows(this.lineDetails, this.headerPreTexts, this.headerPostTexts);
        const printPaginated = this.paginateForPrint(printRows);
        this.paginatedContent = printPaginated.pages;
        this.summaryOnExtraPage = this.div1 !== '400' && printPaginated.summaryOnExtraPage;
        this.cdr.detectChanges();

        // Print popup is about:blank — rewrite relative asset URLs (torna/header/footer) to absolute
        const baseHref =
            document.querySelector('base')?.href || `${window.location.origin}${window.location.pathname.replace(/\/[^/]*$/, '/')}`;
        let printContents = this.pickingListElement.nativeElement.innerHTML as string;
        printContents = printContents.replace(/(src=["'])(?!https?:|data:|blob:)([^"']+)/gi, (_m, prefix: string, path: string) => {
            try {
                return prefix + new URL(path, baseHref).href;
            } catch {
                return prefix + path;
            }
        });

        this.paginatedContent = savedPages;
        this.summaryOnExtraPage = savedSummary;
        this.cdr.detectChanges();

        const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
            .map((node) => node.outerHTML)
            .join('\n');
        const popupWin = window.open('', '_blank', 'width=1000,height=800');
        if (!popupWin) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Print blocked',
                detail: 'Please allow pop-ups for this site to print the picking list.',
                life: 5000
            });
            return;
        }
        popupWin.document.open();
        popupWin.document.write(`
        <html>
          <head>
            <title>Picking List</title>
            <base href="${baseHref}">
            ${styles}
            <style>
              @page { size: A4; margin: 0; }
              html, body {
                background: #fff !important;
                margin: 0 !important;
                padding: 0 !important;
              }
              /*
                Each .invoice-wrapper = one A4 page with header at top and footer at bottom
                (same layout as Picking List_1011.pdf). Header/footer are pinned on every page
                via flex + exact page height — not CSS position:fixed (that clips mid-page rows).
              */
              .invoice-wrapper {
                box-shadow: none !important;
                border: none !important;
                border-radius: 0 !important;
                margin: 0 !important;
                padding: 0 !important;
                width: 210mm !important;
                height: 297mm !important;
                min-height: 297mm !important;
                max-height: 297mm !important;
                overflow: hidden !important;
                display: flex !important;
                flex-direction: column !important;
                position: relative !important;
                page-break-after: always;
                break-after: page;
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                box-sizing: border-box !important;
              }
              .invoice-wrapper:last-child,
              .invoice-wrapper.last-page {
                page-break-after: auto !important;
                break-after: auto !important;
              }
              .header-top {
                position: relative !important;
                top: auto !important;
                left: auto !important;
                right: auto !important;
                flex: 0 0 auto !important;
                height: auto !important;
                width: 100% !important;
                padding: 4px 10px 0 10px !important;
                background: #fff !important;
                z-index: 2 !important;
              }
              .footer {
                position: relative !important;
                bottom: auto !important;
                left: auto !important;
                right: auto !important;
                flex: 0 0 auto !important;
                height: auto !important;
                width: 100% !important;
                margin-top: auto !important;
                padding: 2px 0 4px 0 !important;
                background: #fff !important;
                z-index: 2 !important;
                box-shadow: none !important;
                border: none !important;
              }
              .main-content {
                position: relative !important;
                flex: 1 1 auto !important;
                margin: 0 !important;
                max-height: none !important;
                overflow: hidden !important;
                padding: 4px 16px 12px 16px !important;
                gap: 6px !important;
                min-height: 0 !important;
              }
              .watermark-footer {
                display: block !important;
                position: absolute !important;
                right: 20px !important;
                bottom: 110px !important; /* sit just above footer — was 58px and hidden under footer */
                width: auto !important;
                height: 32px !important;
                z-index: 20 !important;
                opacity: 1 !important;
                pointer-events: none !important;
              }
              .watermark-footer img {
                height: 32px !important;
                opacity: 0.25 !important;
                display: block !important;
              }
              .logo, .footer-logo {
                max-width: 100% !important;
                height: auto !important;
                display: block !important;
              }
              .items-table {
                width: 100% !important;
                border-collapse: collapse !important;
                table-layout: fixed !important;
              }
              .items-table th,
              .items-table td {
                padding: 2px 4px !important;
                line-height: 1.25 !important;
              }
              .items-table th:nth-child(1), .items-table td:nth-child(1) { width: 7% !important; }
              .items-table th:nth-child(2), .items-table td:nth-child(2) { width: 11% !important; }
              .items-table th:nth-child(3), .items-table td:nth-child(3) { width: 9% !important; }
              .items-table th:nth-child(4), .items-table td:nth-child(4) { width: 49% !important; }
              .items-table th:nth-child(5), .items-table td:nth-child(5) { width: 10% !important; }
              .items-table th:nth-child(6), .items-table td:nth-child(6) { width: 14% !important; }
              /* Allow long designations to wrap to a 2nd row; pagination budgets for that */
              .items-table td:nth-child(4) {
                text-align: left !important;
                white-space: normal !important;
                word-break: break-word !important;
                overflow-wrap: anywhere !important;
              }
              .prepost-block {
                margin: 4px 0 6px 0 !important;
                padding: 4px 8px !important;
                border-left: 3px solid #555 !important;
                font-size: 12px !important;
                line-height: 1.3 !important;
                white-space: pre-wrap !important;
                word-break: break-word !important;
              }
              .prepost-block__label {
                font-weight: 700 !important;
                margin-right: 4px !important;
              }
              tr.prepost-row td {
                padding: 1px 4px 1px 18px !important;
                font-size: 11px !important;
                font-style: italic !important;
                line-height: 1.25 !important;
                white-space: pre-wrap !important;
                word-break: break-word !important;
                border: none !important;
              }
              .address-invoice-row {
                margin-bottom: 2px !important;
              }
              .total-row {
                margin-top: 4px !important;
                margin-bottom: 4px !important;
              }
              .p-toolbar, .p-button-sm, .p-button-secondary, .sticky-toolbar {
                display: none !important;
              }
            </style>
          </head>
          <body onload="window.print(); window.close();">
            ${printContents}
          </body>
        </html>
      `);
        popupWin.document.close();
    }
    // exportToPDF() {
    //   this.messageService.add({
    //     severity: 'success',
    //     summary: 'Exporting to PDF',
    //     detail: 'Please wait while the picking list is being prepared for PDF export.',
    //     life: 10000
    //   });
    //   const element = this.pickingListElement.nativeElement;
    //   html2canvas(element, { scale: 1.5 }).then((canvas) => {
    //     const imgData = canvas.toDataURL('image/jpeg', 0.95);
    //     const pdf = new jsPDF('p', 'mm', 'a4');
    //     const pageHeight = pdf.internal.pageSize.getHeight(); // ≈ 297mm
    //     const pageWidth = pdf.internal.pageSize.getWidth();   // ≈ 210mm

    //     const imgProps = pdf.getImageProperties(imgData);
    //     const imgHeight = (imgProps.height * pageWidth) / imgProps.width;

    //     let position = 0;
    //     let remainingHeight = imgHeight;

    //     // Add first page
    //     pdf.addImage(imgData, 'JPEG', 0, position, pageWidth, imgHeight);
    //     remainingHeight -= pageHeight;

    //     // Add more pages only if remaining content is larger than a threshold
    //     while (remainingHeight > 1) { // 👈 Fix: threshold prevents extra blank page
    //       position -= pageHeight;
    //       pdf.addPage();
    //       pdf.addImage(imgData, 'JPEG', 0, position, pageWidth, imgHeight);
    //       remainingHeight -= pageHeight;
    //     }

    //     pdf.save('Picking List.pdf');
    //   });
    // }

    exportToPDF() {
        this.messageService.add({
            severity: 'success',
            summary: 'Exporting to PDF',
            detail: 'Please wait while the invoice is being prepared for PDF export.',
            life: 3000
        });

        const element = this.pickingListElement.nativeElement;
        element.classList.add('pdf-export'); // Add class for PDF styling

        // Get all invoice pages
        const wrappers: HTMLElement[] = Array.from(element.querySelectorAll('.invoice-wrapper'));
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        // Helper to render each page
        const renderPage = (idx: number) => {
            if (idx >= wrappers.length) {
                pdf.save('Picking List.pdf');
                element.classList.remove('pdf-export');
                return;
            }
            html2canvas(wrappers[idx], { scale: 2 }).then((canvas) => {
                const imgData = canvas.toDataURL('image/jpeg', 1.0);
                const imgProps = pdf.getImageProperties(imgData);
                const imgWidth = pageWidth;
                const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

                if (idx > 0) pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight > pageHeight ? pageHeight : imgHeight);

                renderPage(idx + 1);
            });
        };

        renderPage(0);
    }

    private hasSyncedReferenceOrderId(referenceOrderId: unknown): boolean {
        const value = (referenceOrderId ?? '').toString().trim();
        return value.length > 0 && /^\d+$/.test(value);
    }

    private revokeDeliveryNoteObjectUrl(): void {
        for (const url of this.deliveryNoteObjectUrls) {
            URL.revokeObjectURL(url);
        }
        this.deliveryNoteObjectUrls = [];
        this.deliveryNotePdfUrls = [];
        this.selectedDeliveryNoteIndex = 0;
    }

    selectDeliveryNote(index: number): void {
        if (index < 0 || index >= this.deliveryNotePdfUrls.length) {
            return;
        }
        this.selectedDeliveryNoteIndex = index;
    }

    private applyDeliveryNotePdfs(payloads: string[]): boolean {
        const pdfs = (payloads || []).map((value) => (value || '').toString().trim()).filter(Boolean);
        if (!pdfs.length) {
            return false;
        }
        this.revokeDeliveryNoteObjectUrl();
        const objectUrls: string[] = [];
        const safeUrls: SafeResourceUrl[] = [];
        for (const payload of pdfs) {
            const blob = this.base64ToBlob(payload, 'application/pdf');
            const objectUrl = URL.createObjectURL(blob);
            objectUrls.push(objectUrl);
            safeUrls.push(this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl));
        }
        this.deliveryNoteObjectUrls = objectUrls;
        this.deliveryNotePdfUrls = safeUrls;
        this.selectedDeliveryNoteIndex = 0;
        this.m3DeliveryNoteLoaded = true;
        this.m3DeliveryNoteError = '';
        this.m3DeliveryNoteRetryVisible = false;
        this.cdr.detectChanges();
        return true;
    }

    private base64ToBlob(base64: string, mime: string = 'application/pdf'): Blob {
        const cleaned = base64.replace(/\s/g, '');
        const binary = atob(cleaned);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return new Blob([bytes], { type: mime });
    }

    private extractHttpErrorMessage(error: any): string {
        try {
            const body = typeof error?.error === 'string' ? JSON.parse(error.error) : error?.error;
            if (body && typeof body === 'object') {
                return (body.message || body.error || '').toString().trim();
            }
            if (typeof error?.error === 'string') {
                return error.error.trim();
            }
        } catch {
            if (typeof error?.error === 'string') {
                return error.error.trim();
            }
        }
        return (error?.message || '').toString().trim();
    }

    fetchPickingListData(orderNumber: string, companyId: string, divisionId: string) {
        // France (300): both Hi/Lo must be >= 44 before viewing picklist (blocks 24/44).
        if (divisionId === '300') {
            if (!this.hasSyncedReferenceOrderId(this.item?.referenceOrderId)) {
                this.useM3DeliveryNote = false;
                this.m3DeliveryNoteError =
                    'Picklist is available after the order is synced to M3.';
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Picklist not available',
                    detail: this.m3DeliveryNoteError,
                    life: 6000
                });
                return;
            }
            if (
                !this.isFrancePicklistStatusReady(
                    this.item?.m3HighestStatus,
                    this.item?.m3LowestStatus
                )
            ) {
                const hi = `${this.item?.m3HighestStatus ?? ''}`.trim() || '—';
                const lo = `${this.item?.m3LowestStatus ?? ''}`.trim() || '—';
                this.useM3DeliveryNote = false;
                this.m3DeliveryNoteError =
                    `Picklist is available when both M3 statuses are 44 or higher (e.g. 44/44, 66/66). Current status: Lo ${lo} / Hi ${hi}. Status 24/44 is not allowed.`;
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Picklist not available',
                    detail: this.m3DeliveryNoteError,
                    life: 6000
                });
                return;
            }
            this.useM3DeliveryNote = true;
            this.fetchM3DeliveryNote(orderNumber, companyId, divisionId);
            return;
        }

        this.useM3DeliveryNote = false;
        this.fetchTornaHtmlPickingList(orderNumber, companyId, divisionId);
    }

    private fetchTornaHtmlPickingList(orderNumber: string, companyId: string, divisionId: string): void {
        this.loaderService.show('Loading picking list...', 'Please wait');
     const url = `${environment.apiUrl}InvoiceAndPicklist/generateOutput?companyId=${companyId}&divisionId=${divisionId}&orderNumber=${orderNumber}&orderMovingStatus=`;

this.http.get<any>(url).subscribe({
  next: (data) => {
                setTimeout(() => this.loaderService.hide(), 300);
    if (this.handleCreditLimitFromGenerateOutput(data, divisionId, () => this.applyPickingListData(data))) {
      return;
    }
    this.applyPickingListData(data);
  },
  error: (error) => {
    this.loaderService.hide();
    console.error('Error while fetching picking list data:', error);
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load picking list data.'
    });
  }
});
    }

    fetchM3DeliveryNote(orderNumber: string, companyId: string, divisionId: string): void {
        this.m3DeliveryNoteLoaded = false;
        this.m3DeliveryNoteError = '';
        this.m3DeliveryNoteRetryVisible = false;
        this.revokeDeliveryNoteObjectUrl();
        this.loaderService.show('Loading picklist...', 'Please wait');

        const url =
            `${environment.apiUrl}m3/delivery-note/base64` +
            `?orderNumber=${encodeURIComponent(orderNumber || '')}` +
            `&company=${encodeURIComponent(companyId || '')}` +
            `&division=${encodeURIComponent(divisionId || '')}`;

        this.http.get<string[]>(url).subscribe({
            next: (pdfs) => {
                this.loaderService.hide();
                try {
                    if (!this.applyDeliveryNotePdfs(pdfs)) {
                        this.openPicklistInProgressDialog(orderNumber, companyId, divisionId);
                    }
                } catch (e) {
                    console.error('Failed to decode picklist PDF', e);
                    this.m3DeliveryNoteError = 'Failed to display the picklist PDF.';
                    this.m3DeliveryNoteRetryVisible = true;
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: this.m3DeliveryNoteError
                    });
                }
            },
            error: (error) => {
                this.loaderService.hide();
                console.error('Error while fetching M3 picklist:', error);
                const status = error?.status;
                if (status === 409) {
                    this.useM3DeliveryNote = false;
                    this.fetchTornaHtmlPickingList(orderNumber, companyId, divisionId);
                    return;
                }
                if (this.isPicklistInProgressError(error)) {
                    this.openPicklistInProgressDialog(orderNumber, companyId, divisionId);
                    return;
                }
                const serverMsg = this.extractHttpErrorMessage(error);
                this.m3DeliveryNoteError = serverMsg || 'Failed to load the M3 picklist.';
                this.m3DeliveryNoteRetryVisible = true;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Picklist unavailable',
                    detail: this.m3DeliveryNoteError,
                    life: 7000
                });
            }
        });
    }

    private isPicklistInProgressError(error: any): boolean {
        const status = error?.status;
        if (status === 404) {
            return true;
        }
        try {
            const body = typeof error?.error === 'string' ? JSON.parse(error.error) : error?.error;
            const msg = (body?.message ?? this.extractHttpErrorMessage(error) ?? '').toString();
            return (
                msg.includes('not found in IDM') ||
                msg.includes('Error while fetching picklist from IDM') ||
                msg.includes('still be processing')
            );
        } catch {
            const msg = this.extractHttpErrorMessage(error);
            return (
                msg.includes('not found in IDM') ||
                msg.includes('Error while fetching picklist from IDM') ||
                msg.includes('still be processing')
            );
        }
    }

    private openPicklistInProgressDialog(
        orderNumber: string,
        companyId: string,
        divisionId: string
    ): void {
        this.m3DeliveryNoteError = '';
        this.m3DeliveryNoteRetryVisible = false;
        this.picklistInProgressParams = { orderNumber, companyId, divisionId };
        this.picklistInProgressDialogVisible = true;
    }

    closePicklistInProgressDialog(): void {
        this.picklistInProgressDialogVisible = false;
        this.picklistInProgressParams = null;
    }

    closePicklistInProgressAndGoBack(): void {
        this.closePicklistInProgressDialog();
        this.goBack();
    }

    retryPicklistFetch(): void {
        const p = this.picklistInProgressParams;
        if (!p) {
            return;
        }
        const url =
            `${environment.apiUrl}m3/delivery-note/base64` +
            `?orderNumber=${encodeURIComponent(p.orderNumber || '')}` +
            `&company=${encodeURIComponent(p.companyId || '')}` +
            `&division=${encodeURIComponent(p.divisionId || '')}`;
        this.loaderService.show('Loading picklist...', 'Please wait');
        this.http.get<string[]>(url).subscribe({
            next: (pdfs) => {
                this.loaderService.hide();
                try {
                    if (!this.applyDeliveryNotePdfs(pdfs)) {
                        // Still not ready — keep dialog open with the same message.
                        return;
                    }
                    this.picklistInProgressDialogVisible = false;
                    this.picklistInProgressParams = null;
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Picklist loaded successfully.',
                        life: 4000
                    });
                } catch (e) {
                    console.error('Failed to decode picklist PDF (retry):', e);
                    this.closePicklistInProgressDialog();
                    this.m3DeliveryNoteError = 'Failed to display the picklist PDF.';
                    this.m3DeliveryNoteRetryVisible = true;
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: this.m3DeliveryNoteError
                    });
                }
            },
            error: (error) => {
                this.loaderService.hide();
                if (this.isPicklistInProgressError(error)) {
                    // Still not ready — keep dialog open.
                    return;
                }
                this.closePicklistInProgressDialog();
                const serverMsg = this.extractHttpErrorMessage(error);
                this.m3DeliveryNoteError = serverMsg || 'Failed to load the M3 picklist.';
                this.m3DeliveryNoteRetryVisible = true;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: this.m3DeliveryNoteError
                });
            }
        });
    }

    retryM3DeliveryNote(): void {
        if (this.picklistInProgressDialogVisible) {
            this.retryPicklistFetch();
            return;
        }
        const orderNumber = this.item?.orderNumber;
        const companyId = this.item?.companyId || this.userService.getCompanyId() || '';
        const divisionId = this.item?.divisionId || this.userService.getDivisionId() || '';
        this.fetchM3DeliveryNote(orderNumber, companyId, divisionId);
    }

    /**
     * Change and Create Invoice + Generate Invoice toolbar/banner actions.
     * Visible when synced and both Hi/Lo are at least 44, but hidden once both are above 44
     * (44/44 and 44/66 stay; 66/66 is hidden). Also hidden for mixed early status (e.g. 24/44).
     */
    get canShowProcessPicklist(): boolean {
        const divisionId = (this.item?.divisionId || this.userService.getDivisionId() || '').toString();
        if (divisionId !== '300') {
            return false;
        }
        if (!this.hasSyncedReferenceOrderId(this.item?.referenceOrderId)) {
            return false;
        }
        const hi = this.parseM3StatusNumber(this.item?.m3HighestStatus);
        const lo = this.parseM3StatusNumber(this.item?.m3LowestStatus);
        if (hi == null || lo == null || hi < 44 || lo < 44) {
            return false;
        }
        return !(hi > 44 && lo > 44);
    }

    /** Both M3 Hi and Lo >= 44 (no upper cap — still shown after 44). */
    private isFrancePicklistStatusReady(highest: unknown, lowest: unknown): boolean {
        const hi = this.parseM3StatusNumber(highest);
        const lo = this.parseM3StatusNumber(lowest);
        return hi != null && lo != null && hi >= 44 && lo >= 44;
    }

    private parseM3StatusNumber(raw: unknown): number | null {
        const s = `${raw ?? ''}`.trim();
        if (!s) {
            return null;
        }
        const n = Number(s);
        return Number.isFinite(n) ? n : null;
    }

    get changedProcessLines(): FranceProcessPicklistLine[] {
        const changed: FranceProcessPicklistLine[] = [];
        for (const group of this.processPicklistGroups) {
            for (const line of group.lines) {
                if (this.isProcessLineChanged(line) && !line.validationError) {
                    changed.push(line);
                }
            }
        }
        return changed;
    }

    get changedProcessLineCount(): number {
        return this.changedProcessLines.length;
    }

    get processPicklistInvalidCount(): number {
        let count = 0;
        for (const group of this.processPicklistGroups) {
            for (const line of group.lines) {
                if (line.validationError) {
                    count++;
                }
            }
        }
        return count;
    }

    get canSubmitProcessPicklist(): boolean {
        return (
            this.changedProcessLineCount > 0 &&
            this.processPicklistInvalidCount === 0 &&
            !this.processPicklistProcessing &&
            !this.processPicklistConfirming
        );
    }

    /** Confirm as-is from popup: need loaded groups, no invalid ship edits, not busy. */
    get canConfirmPicklistWithoutChanges(): boolean {
        return (
            this.processPicklistGroups.length > 0 &&
            this.processPicklistInvalidCount === 0 &&
            !this.processPicklistProcessing &&
            !this.processPicklistConfirming
        );
    }

    get confirmOnlyPicklistCount(): number {
        return this.confirmOnlyPicklists?.length || 0;
    }

    get processPicklistProcessButtonLabel(): string {
        if (this.processPicklistProcessing) {
            return 'Processing...';
        }
        if (this.processPicklistInvalidCount > 0) {
            return 'Fix invalid quantities';
        }
        if (this.changedProcessLineCount === 0) {
            return 'No changes to process';
        }
        return `Process ${this.changedProcessLineCount} Changes`;
    }

    /** Changed when Ship Qty differs from Picked (TRQT). */
    isProcessLineChanged(line: FranceProcessPicklistLine): boolean {
        if (line.editableQuantity === null || line.editableQuantity === undefined) {
            return true;
        }
        return Number(line.editableQuantity) !== Number(line.originalQuantity);
    }

    /** Max allowed Ship Qty = min(Picked, Ordered). */
    maxShipQty(line: FranceProcessPicklistLine): number {
        const picked = Number(line.originalQuantity);
        const ordered = Number(line.orderedQuantity);
        const safePicked = Number.isNaN(picked) || picked < 0 ? 0 : picked;
        const safeOrdered = Number.isNaN(ordered) || ordered < 0 ? safePicked : ordered;
        return Math.min(safePicked, safeOrdered);
    }

    lineMatchesProcessSearch(line: FranceProcessPicklistLine): boolean {
        const q = (this.processPicklistSearch || '').trim().toLowerCase();
        if (!q) {
            return true;
        }
        const fields = [
            line.itemNumber,
            line.itemDescription,
            line.bano,
            line.whsl,
            line.orderLine
        ];
        return fields.some((f) => (f || '').toString().toLowerCase().includes(q));
    }

    getVisibleProcessLines(group: FranceProcessPicklistGroup): FranceProcessPicklistLine[] {
        const lines = group?.lines || [];
        return lines.filter((line) => this.lineMatchesProcessSearch(line));
    }

    private sortProcessLinesByItemNumber(lines: FranceProcessPicklistLine[]): void {
        lines.sort((a, b) =>
            (a.itemNumber || '').localeCompare(b.itemNumber || '', undefined, {
                numeric: true,
                sensitivity: 'base'
            })
        );
    }

    private resetProcessPicklistSearch(): void {
        this.processPicklistSearch = '';
    }

    /** Unshipped remainder against Picked (TRQT − Ship Qty) → M3 QTYO. */
    remainingQty(line: FranceProcessPicklistLine): number {
        const picked = Number(line.originalQuantity) || 0;
        const ship = Number(line.editableQuantity);
        if (Number.isNaN(ship)) {
            return picked;
        }
        return Math.max(0, picked - ship);
    }

    onProcessShipQtyChange(line: FranceProcessPicklistLine): void {
        if (line.editableQuantity === null || line.editableQuantity === undefined || line.editableQuantity === ('' as any)) {
            line.validationError = 'Ship Qty is required';
            return;
        }
        const ship = Number(line.editableQuantity);
        const picked = Number(line.originalQuantity);
        const ordered = Number(line.orderedQuantity);
        if (Number.isNaN(ship)) {
            line.validationError = 'Enter a valid number';
            return;
        }
        if (ship < 0) {
            line.validationError = 'Cannot be negative';
            return;
        }
        if (Number.isNaN(picked) || picked < 0) {
            line.validationError = 'Picked quantity is invalid';
            return;
        }
        if (ship > picked) {
            line.validationError = `Max ${picked} (picked)`;
            return;
        }
        if (!Number.isNaN(ordered) && ordered >= 0 && ship > ordered) {
            line.validationError = `Max ${ordered} (ordered)`;
            return;
        }
        line.editableQuantity = ship;
        line.validationError = '';
    }

    openProcessPicklist(): void {
        if (!this.canShowProcessPicklist || this.processPicklistLoading || this.processPicklistProcessing) {
            return;
        }
        const orderNumber = this.item?.orderNumber;
        const companyId = this.item?.companyId || this.userService.getCompanyId() || '';
        const divisionId = this.item?.divisionId || this.userService.getDivisionId() || '';
        const warehouse =
            (this.item?.warehouseId || this.item?.warehouse || this.inMemoryStorage.get('warehouseId') || '')
                .toString()
                .trim();
        this.processPicklistLoading = true;
        this.processPicklistResults = [];
        this.processPicklistProgress = '';
        this.resetProcessPicklistSearch();
        this.loaderService.show('Loading picklists...', 'Please wait');

        let url =
            `${environment.apiUrl}m3/france-picklist/preview` +
            `?orderNumber=${encodeURIComponent(orderNumber || '')}` +
            `&company=${encodeURIComponent(companyId || '')}` +
            `&division=${encodeURIComponent(divisionId || '')}`;
        if (warehouse) {
            url += `&warehouse=${encodeURIComponent(warehouse)}`;
        }

        this.http.get<any>(url).subscribe({
            next: (data) => {
                this.loaderService.hide();
                this.processPicklistLoading = false;
                this.processPicklistPreviewOrno = data?.referenceOrderId || this.item?.referenceOrderId || '';
                this.processPicklistGroups = (data?.picklists || []).map((g: any) => {
                    const lines = (g.lines || []).map((line: any) => {
                        const picked = Number(line.originalQuantity ?? 0);
                        const orderedRaw = line.orderedQuantity;
                        const ordered =
                            orderedRaw === null || orderedRaw === undefined || orderedRaw === ''
                                ? picked
                                : Number(orderedRaw);
                        const safePicked = Number.isNaN(picked) ? 0 : picked;
                        const safeOrdered = Number.isNaN(ordered) ? safePicked : ordered;
                        return {
                            dlix: String(g.dlix ?? ''),
                            plsx: String(g.plsx ?? ''),
                            plrn: String(line.plrn ?? ''),
                            orderLine: String(line.orderLine ?? ''),
                            itemNumber: String(line.itemNumber ?? ''),
                            itemDescription: String(line.itemDescription ?? ''),
                            orderedQuantity: safeOrdered,
                            originalQuantity: safePicked,
                            // Ship Qty defaults to Picked (no change until user edits).
                            editableQuantity: safePicked,
                            whlo: String(line.whlo ?? ''),
                            whsl: String(line.whsl ?? ''),
                            bano: String(line.bano ?? ''),
                            camu: String(line.camu ?? ''),
                            validationError: '',
                            processingStatus: ''
                        } as FranceProcessPicklistLine;
                    });
                    this.sortProcessLinesByItemNumber(lines);
                    return {
                        dlix: String(g.dlix ?? ''),
                        plsx: String(g.plsx ?? ''),
                        status: String(g.status ?? ''),
                        lines
                    } as FranceProcessPicklistGroup;
                });
                const totalLines = this.processPicklistGroups.reduce(
                    (sum, g) => sum + (g.lines?.length || 0),
                    0
                );
                if (!this.processPicklistGroups.length || totalLines === 0) {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'No picklists',
                        detail: `Unable to retrieve M3 pick lines for order ${this.processPicklistPreviewOrno || orderNumber}.`,
                        life: 6000
                    });
                    return;
                }
                this.processPicklistDialogVisible = true;
                this.cdr.detectChanges();
            },
            error: (error) => {
                this.loaderService.hide();
                this.processPicklistLoading = false;
                const msg =
                    this.extractHttpErrorMessage(error) ||
                    `Unable to retrieve M3 picklist for order ${orderNumber}.`;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Change and Create Invoice',
                    detail: msg,
                    life: 7000
                });
            }
        });
    }

    closeProcessPicklistDialog(): void {
        if (this.processPicklistProcessing || this.processPicklistConfirming) {
            return;
        }
        this.processPicklistDialogVisible = false;
        this.processPicklistConfirmVisible = false;
        this.confirmOnlyDialogVisible = false;
        this.resetProcessPicklistSearch();
    }

    requestProcessPicklistChanges(): void {
        if (!this.canSubmitProcessPicklist) {
            if (this.processPicklistInvalidCount > 0) {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Validation',
                    detail: 'Fix invalid ship quantities before processing.',
                    life: 4000
                });
            }
            return;
        }
        this.processPicklistConfirmVisible = true;
    }

    confirmAndProcessPicklistChanges(): void {
        this.processPicklistConfirmVisible = false;
        this.executeProcessPicklistChanges();
    }

    requestConfirmPicklistFromToolbar(): void {
        if (!this.canShowProcessPicklist || this.processPicklistConfirming || this.processPicklistProcessing) {
            return;
        }
        this.confirmOnlySource = 'toolbar';
        this.confirmOnlyPicklists = [];
        this.confirmOnlyDialogVisible = true;
    }

    requestConfirmPicklistFromPopup(): void {
        if (!this.canConfirmPicklistWithoutChanges) {
            return;
        }
        if (this.changedProcessLineCount > 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Unsaved ship changes',
                detail: 'Clear ship qty edits or use “Process Changes” first. Confirm without changes ignores ship edits.',
                life: 6000
            });
        }
        this.confirmOnlySource = 'popup';
        this.confirmOnlyPicklists = this.processPicklistGroups
            .filter((g) => g.dlix && g.plsx)
            .map((g) => ({ dlix: g.dlix, plsx: g.plsx }));
        this.confirmOnlyDialogVisible = true;
    }

    executeConfirmPicklistOnly(): void {
        this.confirmOnlyDialogVisible = false;
        const orderNumber = this.item?.orderNumber;
        const companyId = this.item?.companyId || this.userService.getCompanyId() || '';
        const divisionId = this.item?.divisionId || this.userService.getDivisionId() || '';
        const warehouse =
            (this.item?.warehouseId || this.item?.warehouse || this.inMemoryStorage.get('warehouseId') || '')
                .toString()
                .trim();

        this.processPicklistConfirming = true;
        this.processPicklistProgress = 'Confirming picklist(s)...';
        this.loaderService.show('Confirming picklist...', 'Please wait');

        const body: any = {
            orderNumber,
            company: companyId,
            division: divisionId,
            picklists: this.confirmOnlyPicklists
        };
        if (warehouse) {
            body.warehouse = warehouse;
        }

        this.http.post<any>(`${environment.apiUrl}m3/france-picklist/confirm`, body).subscribe({
            next: (res) => {
                this.loaderService.hide();
                this.processPicklistConfirming = false;
                this.processPicklistProgress = '';
                this.processPicklistResults = res?.results || [];

                if (this.isFrancePicklistApiSuccess(res)) {
                    this.processPicklistDialogVisible = false;
                    this.openFranceConfirmSuccessDialog(
                        res?.message || 'Picklist confirmed successfully.'
                    );
                    return;
                }
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Confirm failed',
                    detail: res?.message || 'Could not confirm picklist.',
                    life: 8000
                });
                this.cdr.detectChanges();
            },
            error: (error) => {
                this.loaderService.hide();
                this.processPicklistConfirming = false;
                this.processPicklistProgress = '';
                const msg = this.extractHttpErrorMessage(error) || 'Failed to confirm picklist.';
                this.messageService.add({
                    severity: 'error',
                    summary: 'Confirm failed',
                    detail: msg,
                    life: 8000
                });
            }
        });
    }

    private executeProcessPicklistChanges(): void {
        const changes = this.changedProcessLines;
        if (!changes.length) {
            return;
        }
        const orderNumber = this.item?.orderNumber;
        const companyId = this.item?.companyId || this.userService.getCompanyId() || '';
        const divisionId = this.item?.divisionId || this.userService.getDivisionId() || '';
        const warehouse =
            (this.item?.warehouseId || this.item?.warehouse || this.inMemoryStorage.get('warehouseId') || '')
                .toString()
                .trim();

        this.processPicklistProcessing = true;
        this.processPicklistProgress = `Processing 1 of ${changes.length} lines...`;
        this.loaderService.show('Processing picklist...', 'Please wait');

        const body: any = {
            orderNumber,
            company: companyId,
            division: divisionId,
            changes: changes.map((line) => ({
                dlix: line.dlix,
                plsx: line.plsx,
                plrn: line.plrn,
                itemNumber: line.itemNumber,
                orderLine: line.orderLine,
                orderedQuantity: line.orderedQuantity,
                // Picked (TRQT) → picked_qty; Ship Qty → ship_qty
                originalQuantity: line.originalQuantity,
                pickedQuantity: line.editableQuantity,
                whlo: line.whlo,
                whsl: line.whsl,
                bano: line.bano,
                camu: line.camu
            }))
        };
        if (warehouse) {
            body.warehouse = warehouse;
        }

        this.http.post<any>(`${environment.apiUrl}m3/france-picklist/process`, body).subscribe({
            next: (res) => {
                this.loaderService.hide();
                this.processPicklistProcessing = false;
                this.processPicklistProgress = '';
                this.processPicklistResults = res?.results || [];

                const byPlrn = new Map<string, any>();
                for (const r of this.processPicklistResults) {
                    if (r?.plrn) {
                        byPlrn.set(String(r.plrn), r);
                    }
                }
                for (const group of this.processPicklistGroups) {
                    for (const line of group.lines) {
                        const r = byPlrn.get(line.plrn);
                        if (r) {
                            line.processingStatus = r.status || '';
                        }
                    }
                }

                if (this.isFrancePicklistApiSuccess(res)) {
                    this.processPicklistDialogVisible = false;
                    // Process includes M3 Confirm — same invoice-pending redirect UX.
                    this.openFranceConfirmSuccessDialog(
                        res?.message || 'Picklist processed successfully.'
                    );
                    return;
                }
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Partial failure',
                    detail: res?.message || 'Processing partially completed.',
                    life: 8000
                });
                this.cdr.detectChanges();
            },
            error: (error) => {
                this.loaderService.hide();
                this.processPicklistProcessing = false;
                this.processPicklistProgress = '';
                const msg = this.extractHttpErrorMessage(error) || 'Failed to process picklist changes.';
                this.messageService.add({
                    severity: 'error',
                    summary: 'Process failed',
                    detail: msg,
                    life: 8000
                });
            }
        });
    }

    /**
     * Torna France picklist API success flag. Also treat all-SUCCESS results as OK
     * in case the boolean field is missing/misnamed in the JSON payload.
     */
    private isFrancePicklistApiSuccess(res: any): boolean {
        if (!res) {
            return false;
        }
        if (res.success === true || res.success === 'true' || res.Success === true) {
            return true;
        }
        const results = Array.isArray(res.results) ? res.results : [];
        if (!results.length) {
            return false;
        }
        return results.every((r: any) => {
            const status = `${r?.status ?? ''}`.toUpperCase();
            return status === 'SUCCESS' || status === 'OK';
        });
    }

    /**
     * Successful Confirm / Process: modal explains invoice is coming, Back goes to Orders1,
     * auto-redirect after 10 seconds. Failures never open this dialog.
     */
    private openFranceConfirmSuccessDialog(apiMessage?: string): void {
        this.clearFranceConfirmRedirectTimer();
        this.franceConfirmNavigating = false;
        this.processPicklistDialogVisible = false;
        this.processPicklistConfirmVisible = false;
        this.confirmOnlyDialogVisible = false;
        this.picklistInProgressDialogVisible = false;
        this.franceConfirmSuccessMessage = (apiMessage || '').trim();
        this.franceConfirmRedirectSeconds =
            GeneratePickingListComponent.FRANCE_CONFIRM_REDIRECT_SECONDS;
        this.franceConfirmSuccessDialogVisible = true;
        this.startFranceConfirmRedirectCountdown();
        this.cdr.detectChanges();
    }

    private startFranceConfirmRedirectCountdown(): void {
        this.clearFranceConfirmRedirectTimer();
        this.franceConfirmRedirectTimer = setInterval(() => {
            if (this.franceConfirmNavigating || !this.franceConfirmSuccessDialogVisible) {
                this.clearFranceConfirmRedirectTimer();
                return;
            }
            if (this.franceConfirmRedirectSeconds <= 1) {
                this.clearFranceConfirmRedirectTimer();
                this.goToOrders1FromFranceConfirmSuccess();
                return;
            }
            this.franceConfirmRedirectSeconds -= 1;
            this.cdr.detectChanges();
        }, 1000);
    }

    private clearFranceConfirmRedirectTimer(): void {
        if (this.franceConfirmRedirectTimer != null) {
            clearInterval(this.franceConfirmRedirectTimer);
            this.franceConfirmRedirectTimer = null;
        }
    }

    /** Back button or auto-timer — single-flight navigate to Orders1. */
    goToOrders1FromFranceConfirmSuccess(): void {
        if (this.franceConfirmNavigating) {
            return;
        }
        this.franceConfirmNavigating = true;
        this.clearFranceConfirmRedirectTimer();
        this.franceConfirmSuccessDialogVisible = false;
        this.navigateToOrders1AfterFranceSuccess(
            this.franceConfirmSuccessMessage || 'Picklist confirmed successfully.'
        );
    }

    /**
     * onHide fires after we set visible=false ourselves — ignore that path via navigating flag.
     * If the dialog is closed another way, still send the user to Orders1.
     */
    onFranceConfirmSuccessDialogHide(): void {
        this.clearFranceConfirmRedirectTimer();
        if (this.franceConfirmNavigating) {
            return;
        }
        this.goToOrders1FromFranceConfirmSuccess();
    }

    /** Close picklist UI and return to Orders1 with a success toast (visible above topbar there). */
    private navigateToOrders1AfterFranceSuccess(detail: string): void {
        this.processPicklistDialogVisible = false;
        this.processPicklistConfirmVisible = false;
        this.confirmOnlyDialogVisible = false;
        this.picklistInProgressDialogVisible = false;
        this.franceConfirmSuccessDialogVisible = false;
        this.clearFranceConfirmRedirectTimer();
        this.skipReleaseLock = true;
        const message = (detail || 'Picklist confirmed successfully.').trim();
        try {
            sessionStorage.setItem('francePicklistSuccessToast', message);
        } catch {
            // ignore storage failures — still navigate
        }
        // Defer so dialog close / CD finish before route change (avoids cancelled navigations).
        setTimeout(() => {
            this.router.navigateByUrl('/Orders1', { replaceUrl: true }).then(
                (ok) => {
                    if (!ok) {
                        console.warn('France picklist: navigate to /Orders1 did not complete');
                        this.franceConfirmNavigating = false;
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: message,
                            life: 5000
                        });
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'Navigation',
                            detail: 'Could not open Orders automatically. Use Back or open Orders1 from the menu.',
                            life: 8000
                        });
                    }
                },
                (err) => {
                    console.error('France picklist: navigate to /Orders1 failed', err);
                    this.franceConfirmNavigating = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: message,
                        life: 5000
                    });
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Navigation',
                        detail: 'Could not open Orders automatically. Use Back or open Orders1 from the menu.',
                        life: 8000
                    });
                }
            );
        }, 0);
    }

    /** Banner when IDM PDF is missing but Process/Confirm are still available. */
    get showFrancePicklistNotReadyBanner(): boolean {
        return (
            !this.creditLimitBlocked &&
            this.useM3DeliveryNote &&
            !this.m3DeliveryNoteLoaded &&
            !this.picklistInProgressDialogVisible
        );
    }

    openProcessPicklistFromNotReady(): void {
        this.closePicklistInProgressDialog();
        if (!this.canShowProcessPicklist) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Change and Create Invoice',
                detail: 'Change and Create Invoice / Generate Invoice are available when both M3 statuses are 44, and hidden when both are above 44. Status 24/44 is not allowed.',
                life: 5000
            });
            return;
        }
        this.openProcessPicklist();
    }

    getLabel(key: string): string {
        return this.labels[this.selectedLanguage][key] || key;
    }

    private applyPickingListData(data: any): void {
        this.supplierDetails = {
            supplerVrno: data.supplierDetails?.vrno || '',
            coa1: data.supplierDetails?.coa1 || '',
            phno: data.supplierDetails?.phno || '',
            coa2: data.supplierDetails?.coa2 || '',
            coa3: data.supplierDetails?.coa3 || '',
            coa4: data.supplierDetails?.coa4 || ''
        };

        this.customerDetails = {
            customerVrno: data.customerDetails?.customerVrno || '',
            CUA1: data.customerDetails?.customerAddress || '',
            CUA2: '',
            CUA3: '',
            CUA4: '',
            phoneNumber: data.customerDetails?.phoneNumber || ''
        };

        this.pickingListDetails = {
            orderNumber: data.invoiceDetails?.orderNumber || '',
            referenceOrderNumber: data.invoiceDetails?.referenceOrderId || '',
            customerNo: data.invoiceDetails?.customerNo || '',
            date: data.invoiceDetails?.date || ''
        };

        this.headerPreTexts = this.normalizeTextList(data?.headerPreTexts);
        this.headerPostTexts = this.normalizeTextList(data?.headerPostTexts);

        this.lineDetails = (data.lineDetails || []).map((line: any) => ({
            position: line.lineNo,
            itemNo: line.itemNo,
            quantity: line.quantity,
            description: line.description,
            size: line.size || '',
            pcs: line.pcs,
            ppuEUR: line.salesPrice,
            discountPercent: line.discountPercent,
            lineamount: line.lineAmount,
            vatCode: line.vatCode,
            grossWeight: line.grossWeight,
            netWeight: line.netWeight,
            itemGroup: line.itemGroup,
            linePreTexts: this.normalizeTextList(line?.linePreTexts),
            linePostTexts: this.normalizeTextList(line?.linePostTexts)
        }));

        const sortedLines = this.lineDetails.sort((a, b) => {
            if (a.itemGroup === '11000' && b.itemGroup !== '11000') return -1;
            if (a.itemGroup !== '11000' && b.itemGroup === '11000') return 1;
            return 0;
        });
        sortedLines.forEach((line, idx) => {
            line.displayNo = idx + 1;
        });

        // Preview: page budget includes line pre/post so rows are not clipped
        this.paginatedContent = this.buildPreviewPages(sortedLines);

        this.summary = {
            grossValue: data.summaryLevelData?.grossValue || 0,
            totalDiscountValue: data.summaryLevelData?.totalDiscountValue || 0,
            vatAmount: data.summaryLevelData?.vatAmount || 0,
            totalOrderValue: data.summaryLevelData?.orderValue || 0
        };
        this.totalGrossWeight = data.summaryLevelData?.totalGrossWeight || 0;
        this.totalNetWeight = data.summaryLevelData?.totalNetWeight || 0;
    }

    /** Returns true when rendering is deferred (dialog shown). */
    private handleCreditLimitFromGenerateOutput(
        data: any,
        divisionId: string,
        onAllowRender: () => void
    ): boolean {
        // Divisions 100 and 300: picklist accessible to everyone — no credit restriction on open/view.
        if (shouldSkipCreditLimitOnPicklist(divisionId)) {
            this.creditLimitBlocked = false;
            return false;
        }

        if (this.creditLimitAcknowledged) {
            this.creditLimitBlocked = false;
            return false;
        }

        logCreditLimitBypassDebug('picking-list', divisionId, this.inMemoryStorage, 'picklist');
        const { action, restriction } = resolveCreditLimitPageAction(data, divisionId, this.inMemoryStorage, 'picklist');

        if (action === 'none') {
            this.creditLimitBlocked = false;
            return false;
        }

        if (!restriction) {
            this.creditLimitBlocked = false;
            return false;
        }

        this.configureCreditLimitDialog(restriction);

        if (action === 'proceed') {
            this.creditLimitBlocked = true;
            this.lineDetails = [];
            this.paginatedContent = [];
            this.summaryOnExtraPage = false;
            this.creditLimitProceedCallback = () => {
                this.creditLimitBlocked = false;
                onAllowRender();
            };
            this.creditLimitProceedDialogVisible = true;
            return true;
        }

        this.creditLimitBlocked = true;
        this.lineDetails = [];
        this.paginatedContent = [];
        this.summaryOnExtraPage = false;
        this.creditLimitContactNote = getCreditLimitBypassContactNote('picklist', divisionId);
        this.creditLimitBlockedDialogVisible = true;
        return true;
    }

    onCreditLimitProceedCancel(): void {
        this.creditLimitProceedDialogVisible = false;
        this.creditLimitProceedCallback = null;
    }

    onCreditLimitProceedAccept(): void {
        const cb = this.creditLimitProceedCallback;
        this.creditLimitProceedDialogVisible = false;
        this.creditLimitProceedCallback = null;
        cb?.();
    }

    onCreditLimitBlockedDialogClose(): void {
        this.creditLimitBlockedDialogVisible = false;
    }

    private configureCreditLimitDialog(restriction: CreditRestrictionResult): void {
        const mapped = mapCreditRestrictionToDialog(restriction);
        this.creditLimitDialogKind = mapped.kind;
        this.creditLimitDialogNood = mapped.nood;
        this.creditLimitDialogOdud = mapped.odud;
        this.creditLimitDialogData = mapped.data;
    }

    get creditLimitProceedNote(): string {
        return formatCreditLimitDialogNote(
            this.creditLimitDialogKind,
            this.creditLimitDialogNood,
            this.creditLimitDialogOdud,
            this.creditLimitDialogData,
            'You may continue.'
        );
    }

    get creditLimitBlockedNote(): string {
        return formatCreditLimitDialogNote(
            this.creditLimitDialogKind,
            this.creditLimitDialogNood,
            this.creditLimitDialogOdud,
            this.creditLimitDialogData,
            '',
            this.creditLimitContactNote
        );
    }

    startLockRefresh() {
        this.refreshOrderLock(); // Call once immediately
        this.lockRefreshInterval = setInterval(() => {
            this.refreshOrderLock();
        }, 30 * 1000); // 30 seconds
    }
    // getPhoneLines(phone: string): string[] {
    //     return phone
    //         ? phone
    //               .split('|')
    //               ?.map((line) => line.trim())
    //               .filter((line) => line)
    //         : [];
    // }
    refreshOrderLock() {
        const orderNumber = this.item?.orderNumber;
        const companyId = this.userService.getCompanyId() || '';
        const divisionId = this.userService.getDivisionId() || '';
        const currentUsername = this.userService.getUserName?.() || 'unknown';

        if (orderNumber && companyId && divisionId && currentUsername) {
            const url = `${environment.apiUrl}orders/lock/lockrefresh?orderNumber=${orderNumber}&companyId=${companyId}&divisionId=${divisionId}&currentUsername=${currentUsername}`;

            // const url = `http://192.168.31.92:8444/orders/lock/lockrefresh?orderNumber=${orderNumber}&companyId=${companyId}&divisionId=${divisionId}&currentUsername=${currentUsername}`;
            this.http.post(url, {}).subscribe({
                next: () => console.log('Order lock refreshed (picking list)'),
                error: () => console.warn('Order lock refresh failed (picking list)')
            });
        }
    }

    releaseOrderLock() {
        const orderNumber = this.item?.orderNumber;
        const companyId = this.userService.getCompanyId() || '';
        const divisionId = this.userService.getDivisionId() || '';
        const currentUsername = this.userService.getUserName?.() || 'unknown';

        if (orderNumber && companyId && divisionId && currentUsername) {
            const url = `${environment.apiUrl}orders/lock/release?orderNumber=${orderNumber}&companyId=${companyId}&divisionId=${divisionId}&currentUsername=${currentUsername}`;

            // const url = `http://192.168.31.92:8444/orders/lock/release?orderNumber=${orderNumber}&companyId=${companyId}&divisionId=${divisionId}&currentUsername=${currentUsername}`;
            this.http.post(url, {}).subscribe({
                next: () => console.log('Order lock released (picking list)'),
                error: () => console.warn('Order lock release failed (picking list)')
            });
        }
    }
    // Add in ngOnDestroy
    ngOnDestroy() {
        console.log('Component destroyed, releasing order lock');

        this.clearFranceConfirmRedirectTimer();
        this.releaseOrderLock();
        this.revokeDeliveryNoteObjectUrl();

        clearInterval(this.lockRefreshInterval);
    }
}
